import { GitMerge } from "lucide-react";
import { Link } from "react-router-dom";

import { CrmPetDiagnosisController } from "../../../../services/controllers/crmPetDiagnosis";
import { useCrmPetDiagnosis } from "../../../../store/crmPetDiagnosis/useCrmPetDiagnosis";
import { DIAGNOSIS_RULE_OPTIONS } from "../constants";
import { formatNumber } from "../helpers";
import { EmptyState } from "../components/EmptyState";
import { RuleBadge } from "../components/RuleBadge";
import { FunnelStage } from "./FunnelStage";
import { SectionCard } from "../components/SectionCard";
import { SectionError } from "../components/SectionError";

export function RecoveryFunnel() {
  const controller = CrmPetDiagnosisController();
  const opportunities = useCrmPetDiagnosis((state) => state.data.opportunities);
  const byRule = useCrmPetDiagnosis((state) => state.data.overviewByRule);
  const loading = useCrmPetDiagnosis((state) => state.data.loading.diagnosisSummary);
  const error = useCrmPetDiagnosis((state) => state.data.errors.diagnosisSummary);
  const filters = useCrmPetDiagnosis((state) => state.data.filters.overview);

  return (
    <SectionCard
      title="Funil por régua"
      description="Oportunidades são atuais. Demais etapas consideram período selecionado; taxas usam clientes contatados como base."
      icon={GitMerge}
    >
      {error && (
        <SectionError message={error} onRetry={() => controller.retrySection("diagnosisSummary")} />
      )}
      {!error && loading && <div className="h-72 animate-pulse rounded-md bg-gray-100" />}
      {!error && !loading && !byRule && <EmptyState title="Sem indicadores por régua" />}
      {!error && !loading && byRule && (
        <div className="grid gap-4 xl:grid-cols-2">
          {DIAGNOSIS_RULE_OPTIONS.map((rule) => {
            const kpis = byRule[rule.value] || {};
            const opportunityCount = opportunities?.byRule?.[rule.value] || 0;
            const contacted = kpis.contactedAttempts || 0;
            const stages = [
              { label: "Oportunidades atuais", value: opportunityCount, className: "bg-scale-600" },
              { label: "Clientes contatados", value: contacted, className: "bg-highlights-500" },
              { label: "Tiveram agendamento", value: kpis.scheduledCustomers, className: "bg-primary", showRate: true, rateTotal: contacted },
              { label: "Finalizaram atendimento", value: kpis.finishedCustomers, className: "bg-success", showRate: true, rateTotal: contacted },
              { label: "Compraram produtos", value: kpis.purchaseCustomers, className: "bg-secondary", showRate: true, rateTotal: contacted },
            ];
            const visualTotal = Math.max(1, ...stages.map((stage) => Number(stage.value || 0)));
            const resultsLink = `/diagnosis/crm-pet?tab=results&resultsPeriod=${filters.period}&resultsFrom=${filters.from}&resultsTo=${filters.to}&resultsRule=${rule.value}`;

            return (
              <article key={rule.value} className="rounded-lg border border-gray-200 p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <RuleBadge ruleCode={rule.value} />
                  <Link className="text-xs font-medium text-primary hover:underline" to={resultsLink}>
                    Ver resultados
                  </Link>
                </div>
                <div className="space-y-4">
                  {stages.map((stage) => (
                    <FunnelStage key={stage.label} stage={stage} total={visualTotal} />
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-200 pt-4 text-sm text-gray-600">
                  <span>Reagendados: <strong>{formatNumber(kpis.rescheduledCustomers)}</strong></span>
                  <span>Cancelados: <strong>{formatNumber(kpis.canceledCustomers)}</strong></span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
